import React from 'react';
import Icon from '../../../components/AppIcon';

const PlannerPreview = ({ 
  plannerType, 
  size, 
  theme, 
  layout, 
  sections, 
  personalization,
  className = "" 
}) => {
  const getThemeColors = (themeId) => {
    const themes = {
      classic: { primary: '#1E3A5F', secondary: '#4A90A4', accent: '#E2E8F0' },
      warm: { primary: '#D97706', secondary: '#F59E0B', accent: '#FEF3C7' },
      nature: { primary: '#059669', secondary: '#10B981', accent: '#D1FAE5' },
      elegant: { primary: '#7C3AED', secondary: '#A78BFA', accent: '#EDE9FE' },
      coral: { primary: '#FF6B6B', secondary: '#FF8E8E', accent: '#FFE5E5' },
      monochrome: { primary: '#374151', secondary: '#6B7280', accent: '#F3F4F6' }
    };
    return themes?.[themeId] || themes?.classic;
  };

  const colors = getThemeColors(theme);

  // NEW: Extract human-readable type summary from layout object
  const getTypeSummary = (layoutObj) => {
    if (!layoutObj || typeof layoutObj !== 'object') {
      return 'Not selected';
    }
    
    const enabledTypes = [];
    
    // Check each main component
    if (layoutObj?.monthPlanner) {
      enabledTypes?.push('Monthly');
    }
    if (layoutObj?.weekPlanner) {
      enabledTypes?.push('Weekly');
    }
    if (layoutObj?.dailyPlanner?.enabled) {
      enabledTypes?.push('Daily');
    }
    
    // Always include yearly overview (it's required)
    if (layoutObj?.yearlyOverview) {
      enabledTypes?.unshift('Yearly'); // Add at beginning
    }
    
    return enabledTypes?.length > 0 ? enabledTypes?.join(', ') : 'Not selected';
  };

  // NEW: Extract human-readable layout style from layout object
  const getLayoutSummary = (layoutObj) => {
    if (!layoutObj || typeof layoutObj !== 'object') {
      return 'Not selected';
    }
    
    // Check daily planner sub-options for layout style
    if (layoutObj?.dailyPlanner?.enabled) {
      if (layoutObj?.dailyPlanner?.schedule) {
        return 'Schedule Only';
      }
      if (layoutObj?.dailyPlanner?.task) {
        return 'Task Only';
      }
      if (layoutObj?.dailyPlanner?.combination) {
        return 'Schedule + Task Combination';
      }
      return 'Daily Planner'; // Fallback if daily enabled but no sub-option
    }
    
    // If no daily planner, just return the type summary as layout
    return getTypeSummary(layoutObj);
  };

  // Safely extract layout value - handle arrays, objects, and strings
  const getLayoutValue = (layoutProp) => {
    if (!layoutProp) return null;
    if (typeof layoutProp === 'string') return layoutProp;
    if (Array.isArray(layoutProp)) return layoutProp?.[0] || null;
    if (typeof layoutProp === 'object') return layoutProp?.value || layoutProp?.id || null;
    return String(layoutProp);
  };

  // Helper function to safely convert value to string for .replace() operations
  const safeStringReplace = (value, searchValue, replaceValue) => {
    if (!value) return '';
    
    // Handle arrays - take first element or empty string
    if (Array.isArray(value)) {
      value = value?.[0] || '';
    }
    
    // Handle objects that might not stringify well
    if (typeof value === 'object' && value !== null) {
      // Try multiple common property names
      value = value?.name || value?.label || value?.value || value?.id || value?.text || '';
    }
    
    // Final safety check - ensure we have a string
    if (typeof value !== 'string') {
      try {
        value = String(value);
      } catch (e) {
        console.warn('Failed to convert value to string:', value);
        return '';
      }
    }
    
    // Now safe to call replace
    try {
      return value?.replace(searchValue, replaceValue);
    } catch (e) {
      console.warn('Replace operation failed:', e);
      return value || '';
    }
  };

  // Helper function to safely extract section name with comprehensive type handling
  const getSectionName = (section) => {
    if (!section) return '';
    
    // If it's already a string, return it
    if (typeof section === 'string') return section;
    
    // If it's an array, take first element
    if (Array.isArray(section)) {
      return getSectionName(section?.[0]);
    }
    
    // If it's an object, try to extract a name property
    if (typeof section === 'object' && section !== null) {
      return section?.name || section?.label || section?.value || section?.id || section?.text || '';
    }
    
    // Last resort - convert to string
    try {
      return String(section);
    } catch (e) {
      console.warn('Failed to extract section name:', section);
      return '';
    }
  };

  const layoutValue = getLayoutValue(layout);

  const renderLayoutPreview = () => {
    switch (layoutValue) {
      case 'hourly':
        return (
          <div className="space-y-1">
            {['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM']?.map((time, index) => (
              <div key={index} className="flex items-center space-x-2 text-xs">
                <span className="w-12 text-muted-foreground">{time}</span>
                <div className="flex-1 h-4 border border-border rounded"></div>
              </div>
            ))}
          </div>
        );
      case 'block':
        return (
          <div className="space-y-2">
            {['Morning', 'Afternoon', 'Evening']?.map((block, index) => (
              <div key={index} className="space-y-1">
                <h4 className="text-xs font-semibold" style={{ color: colors?.primary }}>
                  {block}
                </h4>
                <div className="h-8 border border-border rounded bg-accent/20"></div>
              </div>
            ))}
          </div>
        );
      case 'list':
        return (
          <div className="space-y-1">
            {[1, 2, 3, 4, 5]?.map((item) => (
              <div key={item} className="flex items-center space-x-2 text-xs">
                <div className="w-3 h-3 border border-border rounded"></div>
                <div className="flex-1 h-3 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        );
      case 'hybrid':
        return (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <h4 className="text-xs font-semibold" style={{ color: colors?.primary }}>
                Schedule
              </h4>
              {['9:00', '10:00', '11:00']?.map((time, index) => (
                <div key={index} className="flex items-center space-x-1 text-xs">
                  <span className="w-8 text-muted-foreground">{time}</span>
                  <div className="flex-1 h-2 border border-border rounded"></div>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              <h4 className="text-xs font-semibold" style={{ color: colors?.primary }}>
                Tasks
              </h4>
              {[1, 2, 3]?.map((item) => (
                <div key={item} className="flex items-center space-x-1 text-xs">
                  <div className="w-2 h-2 border border-border rounded"></div>
                  <div className="flex-1 h-2 bg-muted rounded"></div>
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div className="text-center text-muted-foreground text-xs">
            Select a layout to see preview
          </div>
        );
    }
  };

  const getSizeRatio = (sizeId) => {
    const ratios = {
      'a4': { width: 210, height: 297 },
      'letter': { width: 216, height: 279 },
      'a5': { width: 148, height: 210 },
      'half-letter': { width: 140, height: 216 }
    };
    return ratios?.[sizeId] || ratios?.a4;
  };

  const sizeRatio = getSizeRatio(size);
  const aspectRatio = sizeRatio?.height / sizeRatio?.width;

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold text-foreground">
          Preview
        </h3>
        <div className="flex items-center space-x-2">
          <Icon name="Eye" size={16} className="text-muted-foreground" />
          <span className="font-caption text-xs text-muted-foreground">
            Live Preview
          </span>
        </div>
      </div>
      {/* Preview Container */}
      <div className="flex justify-center">
        <div 
          className="bg-white border-2 border-border rounded-lg shadow-lg overflow-hidden"
          style={{ 
            width: '200px', 
            height: `${200 * aspectRatio}px`,
            borderColor: colors?.primary 
          }}
        >
          {/* Header */}
          <div 
            className="p-3 text-white text-center"
            style={{ backgroundColor: colors?.primary }}
          >
            <h4 className="font-heading text-sm font-semibold">
              {personalization?.name || 'Your Name'}
            </h4>
            <p className="text-xs opacity-90">
              {personalization?.year || new Date()?.getFullYear()}
            </p>
          </div>

          {/* Content Area */}
          <div className="p-3 space-y-3 flex-1">
            {/* Date Header */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold">October 30, 2024</span>
              <div className="flex space-x-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: colors?.secondary }}></div>
                <div className="w-2 h-2 rounded-full bg-muted"></div>
                <div className="w-2 h-2 rounded-full bg-muted"></div>
              </div>
            </div>

            {/* Layout Preview */}
            {renderLayoutPreview()}

            {/* Additional Sections Preview */}
            {sections?.length > 0 && (
              <div className="border-t border-border pt-2 space-y-1">
                {sections?.slice(0, 3)?.map((section, idx) => {
                  // Safely extract section name using the new helper
                  const sectionName = getSectionName(section);
                  
                  // Skip empty sections
                  if (!sectionName) return null;
                  
                  return (
                    <div key={idx} className="flex items-center space-x-2">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: colors?.secondary }}
                      ></div>
                      <span className="text-xs text-muted-foreground capitalize">
                        {safeStringReplace(sectionName, /([A-Z])/g, ' $1')?.trim()}
                      </span>
                    </div>
                  );
                })}
                {sections?.length > 3 && (
                  <div className="text-xs text-muted-foreground">
                    +{sections?.length - 3} more sections
                  </div>
                )}
              </div>
            )}

            {/* Bottom Quote Preview */}
            {personalization?.includeBottomQuotes && (
              <div className="p-2 border-t border-border bg-muted/30">
                <div className="flex items-center justify-center">
                  <Icon name="Quote" size={8} className="text-muted-foreground mr-1" />
                  <p className="text-xs italic text-center text-muted-foreground">
                    "Daily inspiration..."
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Preview Details */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Type:</span>
          <span className="font-medium text-foreground capitalize">
            {getTypeSummary(layout)}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Size:</span>
          <span className="font-medium text-foreground uppercase">
            {size ? String(size) : 'Not selected'}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Theme:</span>
          <div className="flex items-center space-x-2">
            <div 
              className="w-3 h-3 rounded-full border border-border"
              style={{ backgroundColor: colors?.primary }}
            ></div>
            <span className="font-medium text-foreground capitalize">
              {theme ? safeStringReplace(theme, /-/g, ' ') : 'Not selected'}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Layout:</span>
          <span className="font-medium text-foreground capitalize">
            {getLayoutSummary(layout)}
          </span>
        </div>
        {personalization?.includeBottomQuotes && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Quotes:</span>
            <div className="flex items-center space-x-2">
              <Icon name="Quote" size={12} className="text-primary" />
              <span className="font-medium text-foreground capitalize">
                {safeStringReplace(String(personalization?.selectedQuoteSet || ''), /-/g, ' ')} • {safeStringReplace(String(personalization?.quoteFrequency || ''), /-/g, ' ')}
              </span>
            </div>
          </div>
        )}
      </div>
      {/* Quote Preview */}
      {personalization?.quote && (
        <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: colors?.accent }}>
          <p className="text-xs italic text-center" style={{ color: colors?.primary }}>
            "{personalization?.quote}"
          </p>
        </div>
      )}
    </div>
  );
};

export default PlannerPreview;